import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, repeatsAClass } from '../skillUsage';

export const echoTrailGhostsSkill: CodeoSkill = {
    name: SKILL_NAMES.echoTrailGhosts,
    kind: 'codeo',
    description:
        'Leave a trail of fading copies behind a moving element, like onion-skinned frames — for ' +
        'showing speed, repetition, or a path something took.',
    documentUsesSkill: check(
        (script, markup) =>
            repeatsAClass(markup) &&
            /forEach/.test(script) &&
            /opacity:\s*0\b/.test(script) &&
            /\bx:|\by:/.test(script),
    ),
    instructions: `# Echo Trail Ghosts

A moving element leaves copies of itself behind, each fainter and further back, and they catch up and vanish as it settles. It reads as speed when the trail is tight, and as *history* when the ghosts are spread out — the path something took, or how many times it happened.

The trail is real duplicate elements running the same tween on a delay, not a blur. That is what lets each ghost be individually placed, coloured, and faded.

## Markup

Ghosts precede the lead element in the DOM so the lead paints on top, and they carry no text of their own where the lead has any.

\`\`\`html
<div id="trail-stage" style="position: absolute; inset: 0;">
  <div class="ghost" style="position: absolute; left: 200px; top: 460px; width: 190px; height: 190px; border-radius: 28px; background: var(--l5-color-2, #38bdf8); opacity: 0.16;"></div>
  <div class="ghost" style="position: absolute; left: 200px; top: 460px; width: 190px; height: 190px; border-radius: 28px; background: var(--l5-color-2, #38bdf8); opacity: 0.28;"></div>
  <div class="ghost" style="position: absolute; left: 200px; top: 460px; width: 190px; height: 190px; border-radius: 28px; background: var(--l5-color-2, #38bdf8); opacity: 0.44;"></div>
  <div id="lead" style="position: absolute; left: 200px; top: 460px; width: 190px; height: 190px; border-radius: 28px; background: var(--l5-color-2, #38bdf8);"></div>
</div>
\`\`\`

## Timeline code

Every ghost runs the lead's tween, offset in time. The offset is the trail length.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const TRAVEL = 1180;
const LAG = 0.07;
const ghostEls = Array.from(document.querySelectorAll('.ghost'));
const START = 0.4;

gsap.set('#lead', { x: 0 });
gsap.set(ghostEls, { x: 0 });

tl.to('#lead', { x: TRAVEL, duration: 0.95, ease: 'power3.out' }, START);

// Ghosts run the same move, each further behind, and fade out as they arrive.
ghostEls.forEach((ghost, index) => {
    // Every ghost starts AFTER the lead — that is what puts the trail behind it. Ghosts are authored
    // faintest first, so the lag shrinks as the index grows: the faintest lags most and sits
    // furthest back. Start a ghost before the lead and the trail runs in front of the object.
    const lag = (ghostEls.length - index) * LAG;
    tl.to(ghost, { x: TRAVEL, duration: 0.95, ease: 'power3.out' }, START + lag);
    tl.to(ghost, { opacity: 0, duration: 0.4, ease: 'power2.out' }, START + 0.8);
});

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Trail length**: \`LAG\` of 0.05–0.09 gives a tight motion trail. Past 0.2s the ghosts read as separate objects, which is the "history" version rather than the "speed" version.
- **Ghost count**: three is enough for speed; five or six for a longer path. Beyond that they overlap into a smear.
- **Stamps instead**: don't fade the ghosts and they become a record of where the element has been — useful for a repeated action or a route.
- **Outline ghosts**: giving them a border and no fill reads as onion-skinning from an animation tool, which is a specific and quite technical register.
- **Colour shift**: tinting older ghosts toward the background rather than just lowering opacity reads better over busy content.
- **Directional blur alternative**: for a subtler effect on a fast move, a short blur ramp on the lead alone is cheaper — see \`${SKILL_NAMES.softFocusResolve}\` for the filter handling.

## Pitfalls

- Ghosts and lead are \`x\`-tweened, so none may carry a CSS \`transform\`; their shared start position lives in \`left\`.
- Every ghost must run the *same* ease and duration as the lead. A different ease makes them cross each other, which reads as a bug.
- Add the lag to the lead's start time, never subtract it. A ghost that begins before the lead is further along the same path at every instant, so the trail renders in front of the object — the exact inverse of the effect, and it still looks plausible in a single still frame.
- Check the lag against the opacity ramp. The ghosts are authored faintest-first, so the lag has to shrink as the index grows; reverse it and the trail fades *toward* the lead, which reads as the object arriving rather than leaving.
- Fade the ghosts out before the scene ends. Trails left sitting behind a settled element read as rendering artifacts.
- Don't put text in the ghosts. Three overlapping copies of a word are unreadable and the frame looks broken.
- The technique claims speed. On a slow, considered move it reads as a smear rather than as motion.
- Each ghost is another full element composited every frame — keep them simple shapes, never cards with shadows.`,
};
